/* eslint-disable custom/no-restricted-translation-jsx-untranslated-children-text */
import React from 'react';

import { Typography } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import { ROUTES } from '@kamernet/core/Routing/routes';
import { ListingsSearchCategory } from '@kamernet/core/Routing/routes/ListingsSearch';

import styles from './Content.module.css';

export const ContentCountryLongTermRentals = () => {
  const { locale } = useIntl();
  const searchCategories = [ListingsSearchCategory.LongTermRental];

  return locale === Locale.en ? (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        Long-Term Rentals in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Long-term rentals in Netherlands are ideal for individuals or families
        looking to settle down with a stable housing arrangement. Whether you’re
        relocating for work, starting a new chapter, or simply looking for a
        place to call home, Kamernet helps you find rentals that match your
        needs. With a wide range of listings across major cities and local
        neighborhoods, you can explore properties that offer comfort,
        convenience, and the security of longer lease terms.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Why Long-Term Rentals Make Sense in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        In Netherlands’s competitive rental market, long-term rentals provide
        much-needed stability for those planning to stay in one place for an
        extended period. Whether you’re starting a new chapter in your career,
        beginning university, or relocating permanently, a long-term lease
        allows you to build a life with fewer interruptions. Unlike short-term
        rentals that may feel temporary or come with higher monthly costs,
        long-term contracts typically offer better pricing, security, and the
        chance to make your space truly your own. For many, it’s the preferred
        way to experience local life, build connections, and feel a sense of
        belonging.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        A Smarter Way to Rent Long Term in Netherlands
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Securing a long-term rental in Netherlands can be challenging due to
        high demand and limited number of available properties. Many platforms
        complicate the process with extra steps or high costs that make it
        harder to find the right place. Kamernet simplifies the process by
        offering a sign-up fee that gives you full access to a wide range of
        listings. There are no agency commissions, no complicated processes, and
        you can reach out to landlords directly. It’s a more straightforward and
        affordable way to find a long-term home that fits your plans and budget.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Why Choose Kamernet for Long-Term Rentals
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Kamernet is the most trusted website for finding long-term rentals in
        the Netherlands having verified listings that are updated regularly.
        Thousands of renters have found a long-term home through Kamernet,
        backed by a strong reputation and years of experience helping people
        settle with confidence. Unlike many rental platforms that slow things
        down with outdated listings or hidden costs, Kamernet helps you get
        started quickly with transparent pricing and dependable options. If
        you’re still searching for a long-term rental in Netherlands, Kamernet
        is here to help you find a place that truly feels like home.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Popular Cities:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'amsterdam', searchCategories },
          Locale.en,
        )}
      >
        Amsterdam Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'rotterdam', searchCategories },
          Locale.en,
        )}
      >
        Rotterdam Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'den-haag', searchCategories },
          Locale.en,
        )}
      >
        Den Haag Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'eindhoven', searchCategories },
          Locale.en,
        )}
      >
        Eindhoven Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'groningen', searchCategories },
          Locale.en,
        )}
      >
        Groningen Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'utrecht', searchCategories },
          Locale.en,
        )}
      >
        Utrecht Long Term Rentals
      </NavLink.Anchor>
      <Typography variant="h3" className={styles.title}>
        Popular Neighbourhoods:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amstelveen', street: 'uilenstede', searchCategories },
          Locale.en,
        )}
      >
        Uilenstede Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amsterdam', street: 'bijlmerdreef', searchCategories },
          Locale.en,
        )}
      >
        Bijlmerdreef Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'rotterdam', street: 'zuidplein', searchCategories },
          Locale.en,
        )}
      >
        Zuidplein Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'leiden', street: 'klikspaanweg', searchCategories },
          Locale.en,
        )}
      >
        Klikspaanweg Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'diemen', street: 'rode-kruislaan', searchCategories },
          Locale.en,
        )}
      >
        Rode Kruislaan Long Term Rentals
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'den-haag', street: 'leyweg', searchCategories },
          Locale.en,
        )}
      >
        Leyweg Long Term Rentals
      </NavLink.Anchor>
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        Lange termijn huren in Nederland
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Lange termijn huren in Nederland is ideaal voor mensen of gezinnen die
        zich ergens willen vestigen met een stabiele woonsituatie. Of je nu
        verhuist voor werk, aan een nieuw hoofdstuk begint of gewoon een plek
        zoekt om thuis te noemen, Kamernet helpt je bij het vinden van woningen
        die aansluiten op jouw wensen. Met een breed aanbod in grote steden én
        lokale buurten kun je woningen ontdekken die comfort, gemak en zekerheid
        bieden dankzij langere huurtermijnen. Denk aan een appartement te huur
        in Nederland voor lange termijn of een ruim huis huren in Nederland voor
        langere tijd.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Waarom lange termijn huren in Nederland een slimme keuze is
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        In de concurrerende huurmarkt van Nederland biedt lange termijn huren de
        stabiliteit die je nodig hebt als je van plan bent om ergens voor
        langere tijd te blijven. Of je nu aan een nieuwe baan begint, gaat
        studeren of je permanent wilt vestigen, een huurcontract voor de lange
        termijn geeft je de ruimte om een leven op te bouwen zonder constante
        onderbrekingen. In tegenstelling tot kort verblijf, dat vaak duurder en
        tijdelijk voelt, zijn lange termijn contracten doorgaans voordeliger en
        bieden ze meer zekerheid. Zo kun je van je woning echt jouw thuis maken,
        of dat nu een appartement huren in Nederland voor lange termijn is of
        een rustig huis met tuin.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Slimmer huren voor de lange termijn in Nederland
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Een huis te huur in Nederland voor lange termijn vinden kan lastig zijn
        door de grote vraag en het beperkte aanbod. Veel platforms maken het
        proces ingewikkeld met extra stappen of hoge kosten. Kamernet maakt het
        eenvoudig: met slechts een eenmalige aanmeldingskost krijg je toegang
        tot een breed en actueel aanbod. Je betaalt geen makelaarskosten, er
        zijn geen onnodige tussenpersonen en je neemt direct contact op met
        verhuurders. Zo wordt het vinden van een geschikte woning stukken
        eenvoudiger, betaalbaarder en transparanter.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Waarom Kamernet ideaal is voor lange termijn huren in Nederland
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        Kamernet is de meest betrouwbare website voor het vinden van lange
        termijn huurwoning in Nederland, met geverifieerde advertenties die
        regelmatig worden bijgewerkt. Duizenden huurders vonden via Kamernet al
        een appartement of huis voor de lange termijn, dankzij onze sterke
        reputatie en jarenlange ervaring. In tegenstelling tot andere
        verhuurplatforms die je ophouden met verouderde advertenties of
        verborgen kosten, biedt Kamernet transparante prijzen en betrouwbare
        opties. Ben je nog op zoek naar een appartement te huur in Nederland
        voor lange termijn? Kamernet helpt je een plek te vinden die echt als
        thuis voelt.
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Populaire steden:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'amsterdam', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Amsterdam
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'rotterdam', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Rotterdam
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'den-haag', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Den Haag
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'eindhoven', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Eindhoven
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'groningen', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Groningen
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchCity.getURL(
          { city: 'utrecht', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Utrecht
      </NavLink.Anchor>
      <Typography variant="h3" className={styles.title}>
        Populaire buurten:
      </Typography>
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amstelveen', street: 'uilenstede', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Uilenstede
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'amsterdam', street: 'bijlmerdreef', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Bijlmerdreef
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'rotterdam', street: 'zuidplein', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Zuidplein
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'leiden', street: 'klikspaanweg', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Klikspaanweg
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'diemen', street: 'rode-kruislaan', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Rode Kruislaan
      </NavLink.Anchor>
      |
      <NavLink.Anchor
        href={ROUTES.ListingsSearchStreet.getURL(
          { city: 'den-haag', street: 'leyweg', searchCategories },
          Locale.nl,
        )}
      >
        Lange termijn huren Leyweg
      </NavLink.Anchor>
    </React.Fragment>
  );
};
